import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, TextField, Button } from '@mui/material';

const ImpactedArea = ({ loading, selectedMunicipality, selectedSubMunicipality, selectedSuco, municipalities, subMunicipalities, sucos }) => {
  // State to manage the full, unfiltered table data
  const [tableData, setTableData] = useState([]);
  // State to manage the active risk filter
  const [activeFilter, setActiveFilter] = useState('All');

  // Memoized colors for legend
  const legendColors = useMemo(() => ({
    NoRisk: "rgb(148, 203, 85)",
    Low: "rgb(243, 235, 18)",
    Medium: "rgb(243, 155, 58)",
    High: "rgb(230, 45, 58)",
    "N/A": "rgb(222,222,223)",
    All: '#f0f0f0',
  }), []);

  // Simulate data fetching on initial component load
  useEffect(() => {
    const staticData = [
      { SlNo: 1, Municipality: 'Aileu', PostAdmin: 'Aileu Vila', Suco: 'Aissirimu', Characteristic: 'NoRisk' },
      { SlNo: 2, Municipality: 'Aileu', PostAdmin: 'Aileu Vila', Suco: 'Bandudato', Characteristic: 'Low' },
      { SlNo: 3, Municipality: 'Aileu', PostAdmin: 'Aileu Vila', Suco: 'Fahiria', Characteristic: 'NoRisk' },
      { SlNo: 4, Municipality: 'Aileu', PostAdmin: 'Aileu Vila', Suco: 'Fatubossa', Characteristic: 'Medium' },
      { SlNo: 5, Municipality: 'Aileu', PostAdmin: 'Aileu Vila', Suco: 'Hoholau', Characteristic: 'High' },
      { SlNo: 6, Municipality: 'Aileu', PostAdmin: 'Aileu Vila', Suco: 'Lahoe', Characteristic: 'NoRisk' },
      { SlNo: 7, Municipality: 'Aileu', PostAdmin: 'Lauisi', Suco: 'Lauisi', Characteristic: 'Low' },
      { SlNo: 8, Municipality: 'Aileu', PostAdmin: 'Lequitura', Suco: 'Lequitura', Characteristic: 'NoRisk' },
      { SlNo: 9, Municipality: 'Aileu', PostAdmin: 'Saboria', Suco: 'Saboria', Characteristic: 'Medium' },
      { SlNo: 10, Municipality: 'Aileu', PostAdmin: 'Seloi Craic', Suco: 'Seloi Craic', Characteristic: 'NoRisk' },
      { SlNo: 11, Municipality: 'Aileu', PostAdmin: 'Seloi Malere', Suco: 'Seloi Malere', Characteristic: 'NoRisk' },
      { SlNo: 12, Municipality: 'Aileu', PostAdmin: 'Suca Liurai', Suco: 'Suca Liurai', Characteristic: 'High' },
      { SlNo: 13, Municipality: 'Aileu', PostAdmin: 'Bocacelo', Suco: 'Bocacelo', Characteristic: 'NoRisk' },
      { SlNo: 14, Municipality: 'Aileu', PostAdmin: 'Cotolau', Suco: 'Cotolau', Characteristic: 'Low' },
      { SlNo: 15, Municipality: 'Aileu', PostAdmin: 'Fatisi', Suco: 'Fatisi', Characteristic: 'Medium' },
    ];
    setTableData(staticData);
  }, []);

  // Filter data based on dropdown selection and button filter
  const combinedFilteredData = useMemo(() => {
    let currentData = tableData;

    if (selectedMunicipality) {
      const muniName = municipalities.find(m => m.adm1_code === selectedMunicipality)?.adm1;
      currentData = currentData.filter(item => item.Municipality === muniName);
    }
    
    if (selectedSubMunicipality) {
      const subMuniName = subMunicipalities.find(s => s.adm2_code === selectedSubMunicipality)?.adm2;
      currentData = currentData.filter(item => item.PostAdmin === subMuniName);
    }
    
    if (selectedSuco) {
      const sucoName = sucos.find(s => s.adm3_code === selectedSuco)?.adm3;
      currentData = currentData.filter(item => item.Suco === sucoName);
    }

    if (activeFilter !== 'All') {
      currentData = currentData.filter(item => item.Characteristic === activeFilter);
    }

    return currentData.sort(
      (a, b) =>
        a.Municipality.localeCompare(b.Municipality) ||
        a.PostAdmin.localeCompare(b.PostAdmin) ||
        a.Suco.localeCompare(b.Suco)
    );
  }, [selectedMunicipality, selectedSubMunicipality, selectedSuco, tableData, municipalities, subMunicipalities, sucos, activeFilter]);


  const columns = useMemo(
    () => {
      const getCharacteristicColor = (characteristic) => {
        return legendColors[characteristic] || legendColors["N/A"];
      };

      return [
        {
          accessorKey: 'SlNo',
          header: 'Sl No',
          size: 50,
          enableColumnFilter: false,
          enableSorting: true,
          Cell: ({ row }) => (row.index + 1)
        },
        {
          accessorKey: 'Municipality',
          header: 'Municipality',
          size: 150,
          Filter: ({ column }) => (
            <TextField
              variant="standard"
              placeholder="Filter by M..."
              value={(column.getFilterValue() ?? '')}
              onChange={e => column.setFilterValue(e.target.value)}
              sx={{ width: '100%' }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          ),
        },
        {
          accessorKey: 'PostAdmin',
          header: 'Post Admins',
          size: 150,
          Filter: ({ column }) => (
            <TextField
              variant="standard"
              placeholder="Filter by P..."
              value={(column.getFilterValue() ?? '')}
              onChange={e => column.setFilterValue(e.target.value)}
              sx={{ width: '100%' }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          ),
        },
        {
          accessorKey: 'Suco',
          header: 'Suco',
          size: 150,
          Filter: ({ column }) => (
            <TextField
              variant="standard"
              placeholder="Filter by S..."
              value={(column.getFilterValue() ?? '')}
              onChange={e => column.setFilterValue(e.target.value)}
              sx={{ width: '100%' }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          ),
        },
        {
          accessorKey: 'Characteristic',
          header: `Characteristic`,
          Cell: ({ cell }) => (
            <span
              style={{
                backgroundColor: getCharacteristicColor(cell.getValue()),
                padding: '2px 5px',
                borderRadius: '3px',
                display: 'inline-block',
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              {cell.getValue()}
            </span>
          ),
          size: 150,
          filterFn: 'includesString',
          Filter: ({ column }) => (
            <TextField
              variant="standard"
              placeholder="Filter by Char..."
              value={(column.getFilterValue() ?? '')}
              onChange={e => column.setFilterValue(e.target.value)}
              sx={{ width: '100%' }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          ),
        },
      ];
    },
    [legendColors]
  );

  const filterButtons = useMemo(() => {
    const riskLevels = ['All', 'NoRisk', 'Low', 'Medium', 'High', 'N/A'];
    return riskLevels.map(level => (
      <Button
        key={level}
        onClick={() => setActiveFilter(level)}
        sx={{
          minWidth: '80px',
          padding: '6px 12px',
          borderRadius: '20px',
          fontWeight: 'bold',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          textTransform: 'none',
          ml: 1,
          mr: 1,
          backgroundColor: legendColors[level],
          color: 'black',
          border: activeFilter === level ? '2px solid black' : '2px solid transparent',
          boxShadow: activeFilter === level ? '0 0 0 2px black' : 'none',
          '&:hover': {
            backgroundColor: legendColors[level],
            opacity: 0.8,
            transform: 'translateY(-2px)',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid black',
            outlineOffset: '2px',
          },
        }}
      >
        {level}
      </Button>
    ));
  }, [activeFilter, legendColors]);

  return (
    <div className="info-content-text">
      <Box sx={{ p: 2, borderTop: '1px solid #ddd', overflow: 'auto', width: '100%' }}>
        <MaterialReactTable
          columns={columns}
          data={combinedFilteredData}
          enableColumnFilters
          enableColumnActions
          enableSorting
          enablePagination
          paginationDisplayMode='pages'
          enableDensityToggle={true}
          enableFullScreenToggle={true}
          enableGlobalFilter
          initialState={{
            columnVisibility: { SlNo: true },
            density: 'compact',
            pagination: { pageSize: 10 },
          }}
          renderTopToolbarCustomActions={() => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                width: '100%',
                p: '0.5rem',
                gap: '1rem',
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Impacted Areaaaa
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                {filterButtons}
              </Box>
            </Box>
          )}
          muiTableContainerProps={{ sx: { maxHeight: '600px' } }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 'bold',
              backgroundColor: '#e0e0e0',
            },
          }}
          renderEmptyRowsFallback={() => (
            <Typography>
              No impact data found.
            </Typography>
          )}
        />
      </Box>
    </div>
  );
};

export default ImpactedArea;